import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, Check, Edit3, XCircle, AlertTriangle, Camera, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import ProgressCaptureModal from './ProgressCaptureModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
export const RISK_BADGE = {
  WITHIN_PLAN: 'bg-emerald-100 text-emerald-700', WATCH: 'bg-amber-100 text-amber-700',
  BEHIND: 'bg-red-100 text-red-700', NO_DATA: 'bg-slate-100 text-slate-500',
};
export const RISK_LABEL = { WITHIN_PLAN: 'Within Plan', WATCH: 'Watch', BEHIND: 'Behind', NO_DATA: 'No Data' };
const STATUS_BADGE = {
  PENDING_REVIEW: 'bg-blue-100 text-blue-700', ACCEPTED: 'bg-emerald-100 text-emerald-700',
  CORRECTED: 'bg-indigo-100 text-indigo-700', REJECTED: 'bg-red-100 text-red-700',
  INSUFFICIENT_EVIDENCE: 'bg-slate-100 text-slate-500',
};

// Shows the 4 distinct progress concepts + variance + risk for an activity, its observations,
// and the human review workflow (Accept / Correct / Reject / Insufficient) with evidence context.
const ProgressReviewDrawer = ({ projectId, activityId, canReview, canCreate, onClose, onChanged }) => {
  const [progress, setProgress] = useState(null);
  const [observations, setObservations] = useState([]);
  const [trend, setTrend] = useState(null);
  const [methodCfg, setMethodCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCapture, setShowCapture] = useState(false);
  const [correctingId, setCorrectingId] = useState(null);
  const [correctVal, setCorrectVal] = useState('');
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, obs, tr, mc] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/activities/${activityId}/progress`).then((r) => r.data),
        axios.get(`${API}/projects/${projectId}/progress/observations`, { params: { activityId, pageSize: 50 } }).then((r) => r.data),
        axios.get(`${API}/projects/${projectId}/activities/${activityId}/progress/trend`).then((r) => r.data),
        axios.get(`${API}/projects/${projectId}/progress/method-config`, { params: { activityId } }).then((r) => r.data),
      ]);
      setProgress(p); setObservations(obs.items || []); setTrend(tr); setMethodCfg(mc);
    } catch (e) { toast.error('Failed to load progress'); } finally { setLoading(false); }
  }, [projectId, activityId]);

  useEffect(() => { load(); }, [load]);

  const doReview = async (obsId, action, reviewedPercent = null, rsn = null) => {
    setActing(true);
    try {
      await axios.post(`${API}/projects/${projectId}/progress/observations/${obsId}/review`,
        { action, reviewedPercent, reason: rsn });
      toast.success(`Observation ${action.toLowerCase()}ed`);
      setCorrectingId(null); setCorrectVal(''); setReason('');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); } finally { setActing(false); }
  };

  const cards = progress ? [
    ['Planned', progress.plannedProgress, 'bg-slate-50'],
    ['Reported', progress.reportedProgress, 'bg-slate-50'],
    ['AI Observed', progress.aiObservedProgress, 'bg-purple-50'],
    ['Reviewed Visual', progress.reviewedVisualProgress, 'bg-emerald-50'],
  ] : [];

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="progress-review-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-slate-50 h-full overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-slate-900 truncate">{progress ? `${progress.activityCode || ''} ${progress.activityName || ''}` : 'Activity Progress'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="progress-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        {loading || !progress ? (
          <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="progress-four-cards">
              {cards.map(([label, val, bg]) => (
                <div key={label} className={`rounded-xl border border-slate-200 p-4 ${bg}`}>
                  <p className="text-2xl font-bold text-slate-900" data-testid={`progress-${label.toLowerCase().replace(/ /g, '-')}`}>{pct(val)}</p>
                  <p className="text-xs text-slate-500 mt-1">{label}{label === 'Reviewed Visual' && val === null ? ' — No Evidence' : ''}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 flex-wrap bg-white rounded-xl border border-slate-200 p-4">
              <div><p className="text-xs text-slate-400">Plan vs Reviewed Visual</p><p className="font-semibold text-slate-800" data-testid="progress-variance">{progress.planVsReviewedVariancePp === null ? '—' : `${progress.planVsReviewedVariancePp} pp`}</p></div>
              <span className={`text-xs px-3 py-1 rounded-full ${RISK_BADGE[progress.riskClassification]}`} data-testid="progress-risk">{RISK_LABEL[progress.riskClassification]}</span>
              <span className="text-xs text-slate-400">Method: {progress.progressMethod} ({progress.methodSource})</span>
              {canCreate && <button onClick={() => setShowCapture(true)} className="ml-auto px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg flex items-center gap-1" data-testid="progress-capture-btn"><Camera className="w-4 h-4" /> Capture Progress</button>}
            </div>
            <p className="text-[11px] text-slate-400">Reviewed Visual is the only authorized value. AI Observed is a proposal, never certified truth. No Evidence ≠ 0% progress.</p>

            {/* Observations + review */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="progress-observations">
              <h3 className="font-semibold text-slate-800 mb-3">Observations & Review</h3>
              {observations.length === 0 ? <p className="text-sm text-slate-400">No progress observations yet.</p> : (
                <div className="space-y-3">
                  {observations.map((o) => (
                    <div key={o.id} className="border border-slate-100 rounded-lg p-3" data-testid="progress-observation-row">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[o.reviewStatus]}`}>{o.reviewStatus.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-slate-500">{o.method}</span>
                        <span className="text-xs text-slate-400">{String(o.observationDate).slice(0, 10)}</span>
                        {o.regressionFlag && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Regression</span>}
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                        <div><span className="text-slate-400">AI:</span> {pct(o.aiEstimatedPercent)}{o.aiConfidence != null ? ` (${Math.round(o.aiConfidence * 100)}%)` : ''}</div>
                        <div><span className="text-slate-400">Proposed:</span> {pct(o.proposedPercent)}</div>
                        <div><span className="text-slate-400">Reviewed:</span> {pct(o.reviewedPercent)}</div>
                      </div>
                      {o.inputQuality && <p className="text-[11px] text-slate-400 mt-1">Input quality: {o.inputQuality}{o.inputQualityReasons?.length ? ` — ${o.inputQualityReasons.join(', ')}` : ''}</p>}
                      {o.reviewReason && <p className="text-[11px] text-slate-500 mt-1">Reason: {o.reviewReason}</p>}

                      {canReview && o.reviewStatus === 'PENDING_REVIEW' && (
                        correctingId === o.id ? (
                          <div className="mt-2 space-y-2 bg-slate-50 rounded p-2">
                            <input type="number" value={correctVal} onChange={(e) => setCorrectVal(e.target.value)} placeholder="Reviewed %" className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="progress-correct-value" />
                            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="progress-correct-reason" />
                            <div className="flex gap-2">
                              <button disabled={acting} onClick={() => doReview(o.id, 'CORRECT', Number(correctVal), reason)} className="px-3 py-1 bg-indigo-600 text-white text-xs rounded" data-testid="progress-correct-save">Save Correction</button>
                              <button onClick={() => setCorrectingId(null)} className="px-3 py-1 border text-xs rounded">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            <button disabled={acting || (o.proposedPercent == null && o.aiEstimatedPercent == null)} onClick={() => doReview(o.id, 'ACCEPT')} className="px-3 py-1 bg-emerald-600 text-white text-xs rounded flex items-center gap-1 disabled:opacity-40" data-testid="progress-accept-btn"><Check className="w-3 h-3" /> Accept</button>
                            <button onClick={() => { setCorrectingId(o.id); setCorrectVal(''); setReason(''); }} className="px-3 py-1 bg-indigo-600 text-white text-xs rounded flex items-center gap-1" data-testid="progress-correct-btn"><Edit3 className="w-3 h-3" /> Correct</button>
                            <button disabled={acting} onClick={() => { const r = prompt('Reason for rejection:'); if (r) doReview(o.id, 'REJECT', null, r); }} className="px-3 py-1 bg-red-600 text-white text-xs rounded flex items-center gap-1" data-testid="progress-reject-btn"><XCircle className="w-3 h-3" /> Reject</button>
                            <button disabled={acting} onClick={() => doReview(o.id, 'INSUFFICIENT', null, 'Marked insufficient by reviewer')} className="px-3 py-1 bg-slate-500 text-white text-xs rounded flex items-center gap-1" data-testid="progress-insufficient-btn"><AlertTriangle className="w-3 h-3" /> Insufficient</button>
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {trend && trend.reviewedTrend.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="progress-trend">
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Reviewed Visual Trend</h3>
                {trend.reviewedTrend.map((t, i) => <div key={i} className="text-sm text-slate-600">{String(t.date).slice(0, 10)}: <b>{t.reviewedPercent}%</b> ({t.method})</div>)}
                <p className="text-[11px] text-slate-400 mt-2">{trend.note}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showCapture && (
        <ProgressCaptureModal projectId={projectId} activityId={activityId} defaultMethod={methodCfg?.method}
          onClose={() => setShowCapture(false)} onCreated={() => { load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
};

export default ProgressReviewDrawer;
